import { createAppQueryClient } from '@repo/sdk'
import { getInitialTheme, type Theme } from '@repo/ui/theme'
import type { QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import type { AppLanguageCode } from '@/lib/i18n'
import { getInitialAppLanguage } from '@/lib/i18n'
import { initSentry } from '@/lib/sentry'
import { ErrorPage, LoadingPage, NotFoundPage } from '@/routes/__root'
import { routeTree } from './routeTree.gen'

export interface RouterContext {
	appLanguage: AppLanguageCode
	theme: Theme
	queryClient: QueryClient
}

export function getRouter() {
	const queryClient = createAppQueryClient()
	const router = createRouter({
		routeTree,
		context: {
			appLanguage: getInitialAppLanguage(),
			theme: getInitialTheme(),
			queryClient
		} satisfies RouterContext,
		defaultPendingMs: 0,
		defaultPreload: 'intent',
		defaultPreloadStaleTime: 0,
		scrollRestoration: true,
		defaultErrorComponent: ErrorPage,
		defaultNotFoundComponent: NotFoundPage,
		defaultPendingComponent: LoadingPage
	})

	// Client-only bootstrap. `StartClient`/`hydrateStart` expose no post-hydrate
	// hook, and calling `hydrateStart()` from `client.tsx` would spawn a second
	// router instance in production. Gating on `router.isServer` keeps SSR clean.
	if (!router.isServer) {
		initSentry(router)
	}

	setupRouterSsrQueryIntegration({
		router,
		queryClient
	})

	return router
}

declare module '@tanstack/react-router' {
	interface Register {
		router: ReturnType<typeof getRouter>
	}
}
